import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  getMongoHealthStatus,
  MongoHealthResponse,
} from '../../../services/api';

const POLL_INTERVAL_MS = 60_000;

interface FetchError {
  id: number;
  message: string;
}

const MongoHealthBanner: React.FC = () => {
  const [health, setHealth] = useState<MongoHealthResponse | null>(null);
  const [errorState, setErrorState] = useState<FetchError | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const lastStatusRef = useRef<MongoHealthResponse['status'] | null>(null);
  const lastErrorRef = useRef<number | null>(null);

  useEffect(() => {
    let isMounted = true;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const loadHealth = async () => {
      try {
        const response = await getMongoHealthStatus();
        if (!isMounted) return;
        setHealth(response);
        setErrorState(null);
      } catch (err) {
        if (!isMounted) return;
        setErrorState({
          id: Date.now(),
          message: 'Unable to contact the MongoDB health service.',
        });
        setHealth(null);
      }
    };

    loadHealth();
    intervalId = setInterval(loadHealth, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  useEffect(() => {
    if (!health) return;
    if (health.status !== lastStatusRef.current) {
      lastStatusRef.current = health.status;
      setDismissed(false);
    }
  }, [health]);

  useEffect(() => {
    if (errorState && errorState.id !== lastErrorRef.current) {
      lastErrorRef.current = errorState.id;
      setDismissed(false);
    } else if (!errorState) {
      lastErrorRef.current = null;
    }
  }, [errorState]);

  const severity = useMemo(() => {
    if (errorState || health?.status === 'unavailable') {
      return 'error';
    }
    if (health?.status === 'degraded') {
      return 'warning';
    }
    return 'success';
  }, [errorState, health]);

  const shouldDisplay =
    (!!errorState || (health && health.status !== 'healthy')) && !dismissed;

  if (!shouldDisplay) {
    return null;
  }

  const issues = health?.mongo?.issues ?? [];
  const lastChecked = health?.timestamp
    ? new Date(health.timestamp).toLocaleString()
    : '--';

  return (
    <Box mb={3}>
      <Collapse in>
        <Alert
          icon={<WarningAmberIcon fontSize="inherit" />}
          severity={severity as 'error' | 'warning' | 'success'}
          action={
            <IconButton
              aria-label="close mongo health warning"
              color="inherit"
              size="small"
              onClick={() => setDismissed(true)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          <AlertTitle>
            {errorState
              ? 'Data services unreachable'
              : health?.status === 'unavailable'
              ? 'Data temporarily unavailable'
              : 'Data service performance issue'}
          </AlertTitle>
          {errorState ? (
            <Typography variant="body2">{errorState.message}</Typography>
          ) : (
            <>
              <Typography variant="body2" gutterBottom>
                {health?.message ??
                  'Portfolio data may be delayed while we reconnect.'}
              </Typography>
              {issues.length > 0 && (
                <Box component="ul" sx={{ mt: 1, pl: 3, mb: 1 }}>
                  {issues.map((issue, idx) => (
                    <li key={`${issue}-${idx}`}>
                      <Typography variant="body2">{issue}</Typography>
                    </li>
                  ))}
                </Box>
              )}
              <Typography variant="caption" display="block">
                Last checked: {lastChecked}
              </Typography>
            </>
          )}
        </Alert>
      </Collapse>
    </Box>
  );
};

export default MongoHealthBanner;
